import type { SupabaseClient } from '@supabase/supabase-js';

/** Rohzeile aus gambio.activity_log, soweit für die Ergebnis-Klassifikation nötig. */
export interface OutcomeRow {
  id: number;
  event: string;
  status: string;
  summary: string | null;
  detail: Record<string, unknown> | null;
}

export type OrderOutcome =
  | { status: 'success'; beleg: string | null; documentId: string | null }
  | { status: 'error'; summary: string }
  | { status: 'timeout' };

export interface AwaitOrderOutcomeOptions {
  shopKey: string;
  idOrder: string;
  /** max(id) der activity_log für dieses ref_id VOR dem Enqueue (id-basiert → kein Uhr-Skew). */
  baselineId: number;
  /** Hartes Timeout in ms (Default 90000). */
  timeoutMs?: number;
  /** Poll-Intervall in ms (Default 3000). */
  intervalMs?: number;
  /** Bricht das Polling ab (z. B. Drawer-Close) → liefert dann { status: 'timeout' }. */
  signal?: AbortSignal;
}

/** Belegnummer aus documentNumber (detail) bzw. der Meldung („Beleg <nr>"). */
function belegOf(r: OutcomeRow): string | null {
  const d = r.detail;
  const num = d?.['documentNumber'] ?? d?.['document_number'];
  if (typeof num === 'string' && num) return num;
  if (typeof num === 'number') return String(num);
  const m = r.summary?.match(/Beleg\s+(\S+)/i);
  return m ? m[1]! : null;
}

/** Vario-Dokument-ID aus detail.result.varioOrderId / documentId (für Deeplinks). */
function documentIdOf(r: OutcomeRow): string | null {
  const d = r.detail;
  if (!d) return null;
  const result = d['result'] as Record<string, unknown> | undefined;
  const id = result?.['varioOrderId'] ?? result?.['documentId'] ?? d['documentId'] ?? d['document_id'];
  if (typeof id === 'string' && id) return id;
  if (typeof id === 'number') return String(id);
  return null;
}

/**
 * Reine Klassifikation: `rows` ist ABSTEIGEND nach id sortiert (jüngste zuerst) und nur die neuen
 * Zeilen dieses Versuchs (id > baseline). Liefert das terminale Ergebnis oder null (= weiter pollen).
 * - order_import success  → success (mit Beleg)
 * - order_import error    → error
 * - order_fix   error     → error (Behebung konnte Artikel nicht anlegen)
 * - order_fix   success   → Zwischenschritt „Artikel angelegt, wird erneut übernommen" → ignorieren
 */
export function classifyOutcomeRows(rows: OutcomeRow[]): OrderOutcome | null {
  for (const r of rows) {
    if (r.event === 'order_import' && r.status === 'success') {
      return { status: 'success', beleg: belegOf(r), documentId: documentIdOf(r) };
    }
    if (r.event === 'order_import' && r.status === 'error') {
      return { status: 'error', summary: r.summary ?? 'Übernahme fehlgeschlagen' };
    }
    if (r.event === 'order_fix' && r.status === 'error') {
      return { status: 'error', summary: r.summary ?? 'Behebung fehlgeschlagen' };
    }
    // order_fix success = Zwischenschritt → nicht terminal, nächste Zeile prüfen.
  }
  return null;
}

/** Abbruchbares Sleep. */
function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise<void>((resolve) => {
    if (signal?.aborted) return resolve();
    const t = setTimeout(resolve, ms);
    signal?.addEventListener('abort', () => { clearTimeout(t); resolve(); }, { once: true });
  });
}

/** Größte activity_log-id für dieses (ref_id, storefront) - als Baseline VOR dem Enqueue lesen. */
export async function latestActivityId(
  supabase: SupabaseClient,
  idOrder: string,
  shopKey: string,
): Promise<number> {
  const { data } = await supabase
    .schema('gambio').from('activity_log')
    .select('id')
    .eq('ref_id', idOrder).eq('storefront', shopKey)
    .order('id', { ascending: false }).limit(1).maybeSingle();
  return (data?.id as number | undefined) ?? 0;
}

/**
 * Pollt gambio.activity_log (neue Zeilen id > baselineId, event in order_import/order_fix) bis ein
 * terminales Auftrags-Ergebnis vorliegt oder das Timeout fällt. Query-Fehler unterbrechen NICHT
 * (best-effort, nächster Tick versucht erneut) - nur das Timeout/Abort beendet die Schleife.
 */
export async function awaitOrderOutcome(
  supabase: SupabaseClient,
  opts: AwaitOrderOutcomeOptions,
): Promise<OrderOutcome> {
  const timeoutMs = opts.timeoutMs ?? 90_000;
  const intervalMs = opts.intervalMs ?? 3_000;
  const deadline = Date.now() + timeoutMs;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    if (opts.signal?.aborted) return { status: 'timeout' };
    const { data, error } = await supabase
      .schema('gambio').from('activity_log')
      .select('id,event,status,summary,detail')
      .eq('ref_id', opts.idOrder).eq('storefront', opts.shopKey)
      .gt('id', opts.baselineId)
      .in('event', ['order_import', 'order_fix'])
      .order('id', { ascending: false }).limit(20);
    if (!error && data) {
      const outcome = classifyOutcomeRows(data as OutcomeRow[]);
      if (outcome) return outcome;
    }
    if (Date.now() > deadline) return { status: 'timeout' };
    await sleep(intervalMs, opts.signal);
  }
}
